import { fabric } from "fabric";
import {
  FianalGenerateVideo,
  addCanvasObject,
  removeCanvasObject,
  updateCanvasObject,
} from "./thunks/editor/canvasThunks";
import {
  sanitizedAvatarObject,
  sanitizedBackgroundColorObject,
  sanitizedBackgroundObject,
  sanitizedGraphicsObject,
  sanitizedImageObject,
  sanitizedTextObject,
} from "./utils/sanitizeCanvasObject";
import {
  setCurrentPreviewSlide,
  setSlideLoading,
  updateBackgroundColorObject,
  updateObject as updateObjectSlide,
  updateSlide,
  updateSlideThumbnail,
} from "./store/slides/slidesSlice";
import { ANIMATIONS, AUDIO_FILE_BASE_URL } from "./constants/general";
import { generateSlideFrame } from "./api/templates";
import {
  generateSlideFramesBody,
  getCreateAvatarVideoBody,
} from "./constants/apiRequestBody";
import { createAvatarVideo } from "./api/avatars";
import { getFromLocal } from "./utils/localStorage";
import { setGenerateLoading, setLoaderValue } from "./store/editor/canvasSlice";
import { showToast } from "./utils/general";
import { saveProject } from "./thunks/dashboardThunks";
import { setProjectLoading } from "./store/project/projectSlice";

// export const scaleFactor = 0.3125;
export const avatarScale = 0.2;
export const canvasWidth = 1280;
export const canvasHeight = 720;

// Function to generate a unique ID
function generateUniqueId() {
  return Math.random().toString(36).substr(2, 9);
}

// Function to add text to the canvas
export async function addText(
  canvas,
  object,
  dispatch,
  alreadyInitialized = false,
  previewMode = false,
  handleAnimationComplete,
  isStopped,
  generateVideo,
  scaleFactor,
  setObjectRendered
) {
  if (!object.text) {
    return console.error("Text string is missing.");
  }
  const toggleLoader = (loading) => {
    if (!previewMode) {
      dispatch(setSlideLoading(!!loading));
    }
  };

  toggleLoader(true);
  let left = object?.left || 100,
    top = object?.top || 100,
    width = object?.width || canvas.getWidth() - 200,
    height = object?.height || 0,
    scaleX = object?.scaleX || 1,
    scaleY = object?.scaleY || 1,
    animationEnd = 0,
    animationStart = 0,
    animationType = "left";
  if (alreadyInitialized) {
    top = canvas.getHeight() * object.topPercent;
    left = canvas.getWidth() * object.leftPercent;
    scaleX = canvas.width * object.scaleXPercent;
    scaleY = canvas.height * object.scaleYPercent;
  }

  if (previewMode && object.animation !== "none") {
    switch (object.animation) {
      case "right":
        // console.log("left")
        animationEnd = left;
        animationStart = -object.width * scaleX;
        animationType = "left";
        break;
      case "left":
        animationEnd = left;
        animationStart = canvas.getWidth();
        animationType = "left";
        break;
      case "up":
        animationEnd = top;
        animationStart = canvas.getHeight();
        animationType = "top";
        break;
      case "down":
        animationEnd = top;
        animationStart = -object.height * scaleY;
        animationType = "top";
        break;
      default:
        // Handle any other cases or provide a default behavior here
        break;
    }
  }

  const totalObject = getTotalObjects(canvas);
  let text;

  /**
   * if object is already rendered in the canvas avoid to add new one.
   * & if it is in preview mode update directly.
   */
  const currentCanvasObject = getCanvasObjectById(canvas, object.id);
  if (currentCanvasObject) {
    text = currentCanvasObject;
    toggleLoader();
    if (setObjectRendered) {
      setObjectRendered();
    }
    // canvas.renderAll();
    if (previewMode) {
      if (objectHasAnimation(currentCanvasObject)) {
        return currentCanvasObject.animate(
          { [animationType]: animationEnd },
          {
            onChange: canvas.renderAll.bind(canvas),
            duration: 2000,
            onComplete: () => handleAnimationComplete(object.id),
            abort: () => isStopped,
          }
        );
      }
      return currentCanvasObject;
    }
  } else if (!alreadyInitialized)
    text = new fabric.Textbox(object.text, {
      left: 100,
      top: 100,
      fontFamily: "Inter",
      fill: "#333",
      type: "text",
      width,
      height,
      ...object,
      fontSize: object.fontSize,
      fontWeight: object.fontWeight,
    });
  else
    text = new fabric.Textbox(object.text, {
      ...object,
      width: width,
      height: height,
      left: left,
      top: top,
      fontSize: object.fontSize,
      fontWeight: object.fontWeight,
      type: "text",
      scaleX: scaleX,
      scaleY: scaleY,
    });

  if (!alreadyInitialized) {
    text.id = generateUniqueId();
    text.animation = ANIMATIONS.NONE;
    text.name = `obj` + totalObject;
  }

  if (previewMode) text.selectable = false;

  if (!previewMode) {
    attachEventListeners(text, canvas, dispatch);
  }

  if (!alreadyInitialized) {
    canvas.setActiveObject(text);
  }
  text.on("scaling", (event) => {
    const scaleFactor = text.scaleY;
    const newFontSize = (object.fontSize * scaleFactor).toFixed(2);

    text.set("fontSize", newFontSize);
  });
  canvas.add(text);
  await new Promise((resolve) => {
    canvas.renderAll();
    resolve();
  });

  if (
    previewMode &&
    (object.animation === "right" || object.animation === "left")
  ) {
    text.set("left", animationStart);
  } else if (
    previewMode &&
    (object.animation === "up" || object.animation === "down")
  ) {
    text.set("top", animationStart);
  }

  if (!alreadyInitialized && !previewMode) {
    canvas.bringToFront(text);

    dispatch(
      addCanvasObject(
        sanitizedTextObject(
          {
            ...text,
            id: text.id,
            type: "text",
          },
          canvas
        )
      )
    );
    dispatch(generateCanvasImage(canvas));
  }
  if (previewMode && object.animation !== "none") {
    text.animate(
      { [animationType]: animationEnd },
      {
        onChange: canvas.renderAll.bind(canvas),
        duration: 2000,
        onComplete: () => handleAnimationComplete(object.id),
        abort: () => isStopped,
      }
    );
  }
  toggleLoader();
  if (setObjectRendered) {
    setObjectRendered();
  }
}

// Function to add an image to the canvas
export async function addImage(
  canvas,
  image,
  dispatch,
  alreadyInitialized = false,
  previewMode = false,
  handleAnimationComplete,
  isStopped,
  generateVideo,
  scaleFactor,
  setObjectRendered
) {
  if (!image.url) {
    console.error("Image url missing");
    return;
  }

  const toggleLoader = (loading) => {
    if (!previewMode) {
      dispatch(setSlideLoading(!!loading));
    }
  };
  toggleLoader(true);

  let left = +image?.left || 100,
    top = +image?.top || 100,
    scaleX = +image?.scaleX || 1,
    scaleY = +image?.scaleY || 1,
    animationEnd = 0,
    animationStart = 0,
    animationType = "left";
  if (alreadyInitialized) {
    top = canvas.getHeight() * (image?.topPercent || 1);
    left = canvas.getWidth() * (image?.leftPercent || 1);

    scaleX = canvas.width * (image?.scaleXPercent || 1);
    scaleY = canvas.height * (image?.scaleYPercent || 1);
  }
  if (previewMode && image.animation !== "none") {
    switch (image.animation) {
      case "right":
        animationEnd = left;
        animationStart = -image.width * scaleX;
        animationType = "left";
        break;
      case "left":
        animationEnd = left;
        animationStart = canvas.getWidth();
        animationType = "left";
        break;
      case "up":
        animationEnd = top;
        animationStart = canvas.getHeight();
        animationType = "top";
        break;
      case "down":
        animationEnd = top;
        animationStart = -image.height * scaleY;
        animationType = "top";
        break;
      default:
        // Handle any other cases or provide a default behavior here
        break;
    }
  }

  const currentCanvasObject = getCanvasObjectById(canvas, image.id);

  if (currentCanvasObject) {
    toggleLoader();
    if (setObjectRendered) {
      setObjectRendered();
    }
    // canvas.renderAll();
    if (previewMode) {
      if (objectHasAnimation(currentCanvasObject))
        return currentCanvasObject.animate(
          { [animationType]: animationEnd },
          {
            onChange: canvas.renderAll.bind(canvas),
            duration: 2000,
            onComplete: () => handleAnimationComplete(image.id),
            abort: () => isStopped,
          }
        );
    }
    return currentCanvasObject;
  }
  const totalObject = getTotalObjects(canvas);
  return fabric.Image.fromURL(
    image.url,
    async (img) => {
      if (!alreadyInitialized)
        img.set({
          left: 100,
          top: 100,
          angle: 0,
          hasRotatingPoint: true,
          name: `obj` + totalObject,
          crossOrigin: "Anonymous",
          ...image,
          type: "images",
        });
      else
        img.set({
          ...image,
          left: left,
          top: top,
          scaleX: scaleX,
          scaleY: scaleY,
          angle: 0,
          hasRotatingPoint: true,
          name: `obj` + totalObject,
          crossOrigin: "Anonymous",
          type: "images",
        });

      if (!alreadyInitialized) {
        img.id = generateUniqueId();
        img.animation = ANIMATIONS.NONE;
        img.scaleToWidth(200); // or any other scaling method
      }
      if (previewMode) img.selectable = false;

      if (!previewMode) {
        attachEventListeners(img, canvas, dispatch);
      }
      if (!alreadyInitialized) {
        canvas.setActiveObject(img);
      }
      canvas.add(img);
      await new Promise((resolve) => {
        canvas.renderAll();
        resolve();
      });

      if (
        previewMode &&
        (image.animation === "right" || image.animation === "left")
      ) {
        img.set("left", animationStart);
      } else if (
        previewMode &&
        (image.animation === "up" || image.animation === "down")
      ) {
        img.set("top", animationStart);
      }

      if (!alreadyInitialized && !previewMode) {
        canvas.bringToFront(img);

        dispatch(
          addCanvasObject(
            sanitizedImageObject(
              {
                ...img,
                id: img.id,
                type: "images",
              },
              canvas
            )
          )
        );
        dispatch(generateCanvasImage(canvas));
      }
      if (previewMode && image.animation !== "none") {
        img.animate(
          { [animationType]: animationEnd },
          {
            onChange: canvas.renderAll.bind(canvas),
            duration: 2000,
            onComplete: () => handleAnimationComplete(image.id),
            abort: () => isStopped,
          }
        );
      }
      toggleLoader();
      if (setObjectRendered) {
        setObjectRendered();
      }
    },
    { crossOrigin: "Anonymous" }
  );
}

export function findExistingObject(canvas, type) {
  let existingObject = null;
  canvas.forEachObject((obj) => {
    if (obj.type === type) {
      existingObject = obj;
      return;
    }
  });
  return existingObject;
}

// Function to add a background image to the canvas
export async function addBackgroundImage(
  canvas,
  image,
  dispatch,
  alreadyInitialized = false,
  previewMode,
  handleAnimationComplete,
  isStopped,
  generateVideo,
  scaleFactor,
  setObjectRendered
) {
  if (!image.url) {
    console.error("Image url missing");
    return;
  }
  const toggleLoader = (loading) => {
    if (!previewMode) {
      dispatch(setSlideLoading(!!loading));
    }
  };
  toggleLoader(true);
  const existingBg = findExistingObject(canvas, "background");
  const existingBgVideo = findExistingObject(canvas, "backgroundVideos");

  const totalObject = getTotalObjects(canvas);

  if (previewMode && existingBg) {
    if (setObjectRendered) {
      setObjectRendered();
    }
    // canvas.renderAll();
    return existingBg;
  }

  return fabric.Image.fromURL(
    image.url,
    async (img) => {
      if (!alreadyInitialized && !previewMode)
        canvas.setBackgroundImage(img, canvas.renderAll.bind(canvas), {
          scaleX: canvas.width / img.width,
          scaleY: canvas.height / img.height,
          top: 0,
          left: 0,
          stretch: false,
          name: `obj` + totalObject,
          ...image,
          crossOrigin: "Anonymous",
          type: "background",
          width: img.width,
          height: img.height,
        });
      else if (previewMode)
        canvas.setBackgroundImage(img, canvas.renderAll.bind(canvas), {
          stretch: false,
          name: `obj` + totalObject,
          ...image,
          top: 0,
          left: 0,
          scaleX: canvas.width / img.width,
          scaleY: canvas.height / img.height,
          crossOrigin: "Anonymous",
          type: "background",
          width: img.width,
          height: img.height,
        });
      else
        canvas.setBackgroundImage(img, canvas.renderAll.bind(canvas), {
          ...img,
          ...image,
          scaleX: canvas.width / img.width,
          scaleY: canvas.height / img.height,
          stretch: false,
          left: 0,
          top: 0,
          width: img.width,
          height: img.height,
          crossOrigin: "Anonymous",
          type: "background",
        });

      if (existingBg) {
        img.id = existingBg.id;
      } else if (existingBgVideo) {
        img.id = existingBgVideo.id;
      } else if (!alreadyInitialized) {
        img.id = generateUniqueId();
      }

      if (existingBg) {
        canvas.remove(existingBg);
      }
      if (existingBgVideo) {
        canvas.remove(existingBgVideo);
      }
      canvas.sendToBack(img);
      await new Promise((resolve) => {
        canvas.renderAll();
        resolve();
      });

      if (!previewMode) {
        attachEventListeners(img, canvas, dispatch);
      }

      img.hasControls = false;
      img.selectable = false;

      if (!previewMode) {
        img.lockMovementX = true;
        img.lockMovementY = true;
        img.lockRotation = true;
        img.lockScalingX = true;
        img.lockScalingY = true;
      }

      img.dirty = true;

      if (!alreadyInitialized && !previewMode) {
        const functionToExecute =
          existingBg || existingBgVideo ? updateCanvasObject : addCanvasObject;

        dispatch(
          functionToExecute(
            sanitizedBackgroundObject(
              {
                ...img,
                id: img.id,
                type: "background",
                height: 1080,
                width: 1920,
              },
              canvas
            )
          )
        );
        dispatch(generateCanvasImage(canvas));
      }
      toggleLoader();
      if (setObjectRendered) {
        setObjectRendered();
      }
    },
    { crossOrigin: "Anonymous" }
  );
}

export async function addBackgroundColor(
  canvas,
  color,
  dispatch,
  alreadyInitialized = false,
  previewMode,
  handleAnimationComplete,
  isStopped,
  generateVideo,
  scaleFactor,
  setObjectRendered
) {
  const toggleLoader = (loading) => {
    if (!previewMode) {
      dispatch(setSlideLoading(!!loading));
    }
  };
  // toggleLoader(true);
  const existingBgColor = findExistingObject(canvas, "backgroundColor");

  const totalObject = getTotalObjects(canvas);

  const existingBgImage = findExistingObject(canvas, "background");
  const existingBgVideo = findExistingObject(canvas, "backgroundVideos");
  if (existingBgImage) {
    console.log("Removing existing background image...");
    removeObject(existingBgImage, canvas, dispatch);
    // canvas.remove(existingBgImage); // Remove the image object from canvas objects
    // canvas.setBackgroundImage(null, canvas.renderAll.bind(canvas)); // Clear the background image
  }

  if (existingBgVideo) {
    console.log("Removing existing background image...");
    removeObject(existingBgVideo, canvas, dispatch);
    // canvas.remove(existingBgImage); // Remove the image object from canvas objects
    // canvas.setBackgroundImage(null, canvas.renderAll.bind(canvas)); // Clear the background image
  }

  if (previewMode && existingBgColor) {
    if (setObjectRendered) {
      setObjectRendered();
    }
    // canvas.renderAll();
    return existingBgColor;
  }

  canvas.setBackgroundColor(
    color?.color || "white",
    canvas.renderAll.bind(canvas)
  );

  // If `img` is undefined, it means the background is set to a color string

  if (existingBgColor) {
    canvas.remove(existingBgColor);
  }

  await new Promise((resolve) => {
    canvas.renderAll();
    resolve();
  });

  const payload = sanitizedBackgroundColorObject(
    {
      id: generateUniqueId(),
      type: "backgroundColor",
      color: color?.color,
    },
    canvas
  );
  if (!alreadyInitialized && !previewMode) {
    dispatch(updateBackgroundColorObject({ data: payload }));
    dispatch(generateCanvasImage(canvas));
  }

  if (setObjectRendered) {
    setObjectRendered();
  }

  toggleLoader();
  console.log("toggleLoader", toggleLoader);
}

// export async function addBackgroundColor(color, canvas, dispatch) {
//   canvas.setBackgroundColor(color, canvas.renderAll.bind(canvas));
//   if (setObjectRendered) {
//     setObjectRendered();
//   }
//   return dispatch(generateCanvasImage(canvas));
// }

// Function to add an avatar to the canvas
export async function addAvatar(
  canvas,
  avatar,
  dispatch,
  alreadyInitialized = false,
  previewMode = false,
  handleAnimationComplete,
  isStopped,
  generateVideo,
  scaleFactor,
  setObjectRendered
) {
  if (!avatar.url) {
    console.error("avatar url missing");
    return;
  }

  const toggleLoader = (loading) => {
    if (!previewMode) {
      dispatch(setSlideLoading(!!loading));
    }
  };
  toggleLoader(true);
  const existingAvatar = findExistingObject(canvas, "avatar");
  if (previewMode && existingAvatar) {
    if (setObjectRendered) {
      setObjectRendered();
    }
    // canvas.renderAll();

    return existingAvatar;
  }
  const isDid = avatar.url.indexOf("d-id.com") >= 0;

  const totalObject = getTotalObjects(canvas);
  let left = avatar?.left || 100,
    top = avatar?.top || 100,
    scaleX = avatar?.scaleX || 1,
    scaleY = avatar?.scaleY || 1;

  return fabric.Image.fromURL(
    avatar.url,
    async (img) => {
      if (existingAvatar && !alreadyInitialized) {
        existingAvatar.width = 500;
        existingAvatar.height = 500;
        scaleX = (existingAvatar.width * existingAvatar.scaleX) / 500;
        scaleY = (existingAvatar.height * existingAvatar.scaleY) / 500;
        existingAvatar.scaleX = img.scaleX;
        existingAvatar.scaleY = img.scaleY;
      }

      top = top;
      left = left;
      scaleX = scaleX;
      scaleY = scaleY;
      if (!alreadyInitialized)
        img.set({
          left: left,
          top: top,
          angle: 0,
          hasRotatingPoint: true,
          name: `obj` + totalObject,
          crossOrigin: "Anonymous",
          ...avatar,
          type: "avatar",
          scaleX: scaleX,
          scaleY: scaleY,
        });
      else
        img.set({
          ...avatar,
          left: left,
          top: top,
          angle: 0,
          hasRotatingPoint: true,
          name: `obj` + totalObject,
          crossOrigin: "Anonymous",
          scaleX: scaleX,
          scaleY: scaleY,
          type: "avatar",
        });
      img.lockRotation = true;

      if (!alreadyInitialized && !existingAvatar) {
        img.id = generateUniqueId();
      } else if (existingAvatar) {
        img.id = existingAvatar.id;
      }

      img.lockScalingFlip = true;
      if (!alreadyInitialized) {
        img.scaleToWidth(250);
        img.scale(avatarScale);
      }

      const maxScale = isDid ? 1.5 : (500 / img.width) * 0.75;

      // if (img.width < 500) {
      //   img.scaleX = 1 - img.width / 500;
      // }
      // if (img.height < 500) {
      //   img.scaleY = 1 - img.height / 500;
      // }
      const didMinScale = 0.3;
      if (isDid) {
        img.scaleX = didMinScale;
        img.scaleY = didMinScale;
      }
      const getScaling = () => {
        if (img.width < 500 || img.height < 500) {
          if (img.width < 500) {
            if (img.scaleX < 1 - img.width / 500) {
              img.scaleX = 1 - img.width / 500;
            }
          }
          if (img.height < 500) {
            if (img.scaleY < 1 - img.height / 500) {
              img.scaleY = 1 - img.height / 500;
            }
          }
        }
        if (isDid) {
          if (img.scaleX < didMinScale) {
            img.scaleX = didMinScale;
          }
          if (img.scaleY < didMinScale) {
            img.scaleY = didMinScale;
          }
        } else {
          if (img.scaleX < 0.2) {
            img.scaleX = 0.2;
          }
          if (img.scaleY < 0.2) {
            img.scaleY = 0.2;
          }
        }

        if (img.scaleX > maxScale) {
          img.scaleX = maxScale;
        }
        if (img.scaleY > maxScale) {
          img.scaleY = maxScale;
        }
      };
      getScaling();
      img.on("scaling", function () {
        getScaling();
      });
      if (!previewMode) {
        attachEventListeners(img, canvas, dispatch);
      }
      if (previewMode) img.selectable = false;
      if (!avatar.square)
        img.clipPath = new fabric.Circle({
          radius: img.width < img.height ? img.width / 2 : img.height / 2,
          originX: "center",
          originY: "center",
        });
      if (!previewMode) {
        img.setControlsVisibility({
          mt: false,
          ml: false,
          mr: false,
          mb: false,
        });
      }
      if (existingAvatar) {
        canvas.remove(existingAvatar);
      }
      if (!alreadyInitialized) {
        canvas.setActiveObject(img);
      }
      canvas.add(img);
      await new Promise((resolve) => {
        canvas.renderAll();
        resolve();
      });

      if (!alreadyInitialized && !previewMode) {
        canvas.bringToFront(img);
        const functionToExecute = existingAvatar
          ? updateCanvasObject
          : addCanvasObject;
        dispatch(
          functionToExecute(
            sanitizedAvatarObject(
              {
                ...avatar,
                ...img,
                id: img.id,
                type: "avatar",
              },
              canvas
            )
          )
        );
        dispatch(generateCanvasImage(canvas));
      }
      toggleLoader(false);
      if (setObjectRendered) {
        setObjectRendered();
      }
    },
    { crossOrigin: "Anonymous" }
  );
}

// Function to add an avatar to the canvas
export async function addGraphics(
  canvas,
  asset,
  dispatch,
  alreadyInitialized = false,
  previewMode = false,
  handleAnimationComplete,
  isStopped,
  generateVideo = false,
  scaleFactor,
  setObjectRendered
) {
  if (!asset.url) {
    return console.error("Asset is missing.");
  }

  const toggleLoader = (loading) => {
    if (!previewMode) {
      dispatch(setSlideLoading(!!loading));
    }
  };
  toggleLoader(true);

  let left = asset?.left || 100,
    top = asset?.top || 100,
    scaleX = asset?.scaleX || 1,
    scaleY = asset?.scaleY || 1,
    animationEnd = 0,
    animationStart = 0,
    animationType = "left";

  if (
    (previewMode || generateVideo) &&
    asset.animation !== "none" &&
    !isStopped
  ) {
    switch (asset.animation) {
      case "right":
        // console.log("left")
        animationEnd = left;
        animationStart = (-asset.width - 50) * scaleX;
        animationType = "left";
        break;
      case "left":
        animationEnd = left;
        animationStart = canvas.getWidth() + 50;
        animationType = "left";
        break;
      case "up":
        animationEnd = top;
        animationStart = canvas.getHeight() + 50;
        animationType = "top";
        break;
      case "down":
        animationEnd = top;
        animationStart = (-asset.height - 50) * scaleY;
        animationType = "top";
        break;
      default:
        // Handle any other cases or provide a default behavior here
        break;
    }
  }

  if (generateVideo) {
    top = top * scaleFactor;
    left = left * scaleFactor;
    scaleX = scaleX * scaleFactor;
    scaleY = scaleY * scaleFactor;
    animationEnd = animationEnd * scaleFactor;
    animationStart = animationStart * scaleFactor;
  }

  const totalObject = getTotalObjects(canvas);
  const currentCanvasObject = getCanvasObjectById(canvas, asset.id);
  if (currentCanvasObject && previewMode) {
    toggleLoader();
    if (setObjectRendered) {
      setObjectRendered();
    }
    // canvas.renderAll();
    if (previewMode) {
      if (objectHasAnimation(currentCanvasObject) && !isStopped)
        return currentCanvasObject.animate(
          { [animationType]: animationEnd },
          {
            onChange: canvas.renderAll.bind(canvas),
            duration: 2000,
            onComplete: () => handleAnimationComplete(asset.id),
            abort: () => isStopped,
          }
        );
    }
    return currentCanvasObject;
  }

  return fabric.loadSVGFromURL(
    asset.url,
    async (objects, options) => {
      const obj = fabric.util.groupSVGElements(objects, options);

      obj.set({
        name: `obj` + totalObject,
        crossOrigin: "Anonymous",
        ...asset,
        left: left,
        top: top,
        scaleX,
        scaleY,
        type: "graphics",
      });

      if (asset?.color) {
        // Apply color if provided
        if ((asset.name || "").toLowerCase().includes("outline")) {
          obj.set("stroke", `${asset?.color} !important`);
        } else {
          obj.set("fill", `${asset?.color} !important`);
        }
        // obj.set("color", `${asset?.color} !important`);
      }

      if (!alreadyInitialized) {
        obj.id = generateUniqueId();
        obj.scale(avatarScale); // or any other scaling method
      }

      if (!previewMode) {
        attachEventListeners(obj, canvas, dispatch);
      }
      if (previewMode) obj.selectable = false;
      if (!alreadyInitialized && !previewMode) {
        canvas.setActiveObject(obj);
      }
      canvas.add(obj);
      await new Promise((resolve) => {
        canvas.renderAll();
        resolve();
      });

      if (
        previewMode &&
        (asset.animation === "right" || asset.animation === "left")
      ) {
        obj.set("left", animationStart);
      } else if (
        previewMode &&
        (asset.animation === "up" || asset.animation === "down")
      ) {
        obj.set("top", animationStart);
      }
      if (!alreadyInitialized && !previewMode) {
        canvas.bringToFront(obj);

        dispatch(
          addCanvasObject(
            sanitizedGraphicsObject(
              {
                ...obj,
                id: obj.id,
                type: "graphics",
              },
              canvas
            )
          )
        );
        dispatch(generateCanvasImage(canvas));
      }
      if (previewMode && asset.animation !== "none") {
        obj.animate(
          { [animationType]: animationEnd },
          {
            onChange: canvas.renderAll.bind(canvas),
            duration: 2000,
            onComplete: () => handleAnimationComplete(asset.id),
            abort: () => isStopped,
          }
        );
      }
      toggleLoader();
      if (setObjectRendered) {
        setObjectRendered();
      }
    },
    null,
    { crossOrigin: "Anonymous" }
  );
}

let updateObjectTimeout;
export const updateObjectSpecs = (obj, canvas, dispatch) => {
  if (!obj.target) return;
  const modifiedObj = sanitizedCanvasObject(obj.target, canvas);
  modifiedObj.widthPercent = modifiedObj.width / canvas.getWidth();
  modifiedObj.heightPercent = modifiedObj.height / canvas.getHeight();
  console.log(modifiedObj);
  if (updateObjectTimeout) {
    clearTimeout(updateObjectTimeout);
  }
  updateObjectTimeout = setTimeout(() => {
    dispatch(updateCanvasObject({ id: modifiedObj.id, ...modifiedObj }));
    dispatch(generateCanvasImage(canvas));
  }, 400);
};

// Generic function to attach event listeners to an object
function attachEventListeners(object, canvas, dispatch) {
  object.on("modified", (obj) => {
    updateObjectSpecs(obj, canvas, dispatch);
  });
  canvas.on("text:changed", function (obj) {
    updateObjectSpecs(obj, canvas, dispatch);
  });

  // object.on("remove", (obj) => {
  //   removeObject(obj, canvas, dispatch) ;
  // });
}

export const getActiveObject = (canvas, dispatch) => {
  return canvas.getActiveObject();
};

export const getObjects = (canvas) => {
  return canvas.getObjects() || [];
};

export const getTotalObjects = (canvas) => {
  return (getObjects(canvas) || []).length;
};

export const getCanvasObjectById = (canvas, id) => {
  return getObjects(canvas).find((obj) => obj.id === id);
};
export const objectHasAnimation = (obj) => {
  return obj.animation && obj.animation !== ANIMATIONS.NONE;
};

export const changePosition = async (
  canvas,
  action,
  dispatch,
  currentSlide
) => {
  const activeObject = getActiveObject(canvas);
  switch (action) {
    case "sendBackward":
      canvas.sendBackwards(activeObject);
      break;
    case "sendToBack":
      canvas.sendToBack(activeObject);
      break;
    case "bringForward":
      canvas.bringForward(activeObject);
      break;
    case "bringToFront":
    default:
      canvas.bringToFront(activeObject);
      break;
  }
  const backgroundImage = findExistingObject(canvas, "background");
  if (backgroundImage) {
    canvas.sendToBack(backgroundImage);
  } else {
    const bgVideo = findExistingObject(canvas, "backgroundVideos");
    canvas.sendToBack(bgVideo);
  }
  if (currentSlide) {
    const totalObjects = canvas.getObjects();
    const slideObjects = currentSlide.objects || [];

    const updatedSlides = (totalObjects || []).map((obj) => {
      const find = slideObjects.find((so) => so.id === obj.id);

      return find;
    });

    dispatch(updateSlide({ objects: updatedSlides }));
  }
  await new Promise((resolve) => {
    canvas.renderAll();
    resolve();
  });
  return dispatch(generateCanvasImage(canvas));
};

let saveTimeout;
export const generateCanvasImage =
  (canvas, force = false) =>
  (dispatch, getState) => {
    const id = getState().project.id;
    if (canvas.getObjects().length === 0 && !force) return;
    const previewURL = canvas.toDataURL();

    var img = new Image();
    img.src = previewURL;

    var scale = 0.3; // Adjust scale factor as needed
    var scaledCanvas = document.createElement("canvas");
    var ctx = scaledCanvas.getContext("2d");
    scaledCanvas.width = 1920;
    scaledCanvas.height = 1080;

    img.onload = function () {
      ctx.drawImage(img, 0, 0, 1920, 1080);
      var scaledDataURL = scaledCanvas.toDataURL("image/png");
      dispatch(updateSlideThumbnail(scaledDataURL));
    };

    return;
  };

export const removeObject = async (object, canvas, dispatch) => {
  canvas.remove(object);
  if ((object.type = "background")) canvas.setBackgroundImage(null, () => {});
  if ((object.type = "backgroundVideo"))
    canvas.setBackgroundImage(null, () => {});
  await new Promise((resolve) => {
    canvas.renderAll();
    resolve();
  });
  dispatch(removeCanvasObject(object));
  return dispatch(generateCanvasImage(canvas, true));
};
let copiedObjects = [];
export const copyObject = (canvas) => {
  copiedObjects = new Array();
  const objects = canvas.getActiveObjects() || [];
  if (objects.length) {
    objects.forEach((object) => {
      const clonedObject = JSON.parse(JSON.stringify(object));

      // const object = fabric.util.object.clone(o);
      const formattedObj = {
        ...clonedObject,
        url: object.url,
        top: object.top,
        left: object.left,
        scaleX: object.scaleX,
        scaleY: object.scaleY,
      };

      if (
        clonedObject.type !== "background" ||
        clonedObject.type !== "backgroundVideos"
      ) {
        copiedObjects.push(formattedObj);
      }
    });
  }
};
export const pastObject = async (canvas, dispatch) => {
  if (copiedObjects.length > 0) {
    await setCanvasSlide(
      {
        canvasData: copiedObjects.map((current) => {
          const clonedObject = current;

          return {
            ...clonedObject,
            left: (clonedObject.left || 100) + 20,
            top: (clonedObject.top || 100) + 20,
            id: "",
          };
        }),
      },
      canvas,
      dispatch,
      false,
      null,
      undefined,
      undefined,
      undefined,
      undefined,
      false
    );
  }
};

export const setCanvasSlide = async (
  object,
  canvas,
  dispatch,
  previewMode = false,
  captureFrame = null,
  objectRenderCompleted,
  isStopped,
  generateVideo,
  scaleFactor,
  alreadyInitialized
) => {
  const canvasData = object.canvasData || object.objects || [];

  const animatedObjects = canvasData.filter(
    (c) => c.animation && c.animation !== ANIMATIONS.NONE
  );

  console.log("animatedObjects :>> ", animatedObjects);

  if (!animatedObjects.length && objectRenderCompleted) {
    objectRenderCompleted.isVideo = true;
    objectRenderCompleted.isAnimated = true;
  }

  // for canvas objects should be same order that set.
  // currentactiveindex would be increment once element loaded on canvas.
  // loadingIndex currently ongoing element on canvas
  let currentActiveIndex = 0;
  let loadingIndex = 0;
  const setCurrentActiveIndex = () => {
    currentActiveIndex = ++currentActiveIndex;
  };
  const setObjectRendered = () => {
    setCurrentActiveIndex();
    objectRenderCompleted.objectLoaded = ++objectRenderCompleted.objectLoaded;
  };

  const handleAnimationComplete = (id) => {
    const currentAnimatedIndex = animatedObjects.findIndex((a) => a.id === id);
    if (currentAnimatedIndex > -1) {
      animatedObjects.splice(currentAnimatedIndex, 1);
    }
    if (!animatedObjects.length && objectRenderCompleted) {
      objectRenderCompleted.isVideo = true;
      objectRenderCompleted.isAnimated = true;
    }
  };

  const setCanvasData = async (obj) => {
    let customFunction;
    switch ((obj.type || "").toLowerCase()) {
      case "avatar":
        if (!generateVideo) {
          customFunction = addAvatar;
        } else {
          if (objectRenderCompleted) {
            setObjectRendered();
          }
        }
        break;
      case "images":
        customFunction = addImage;
        break;
      case "graphics":
        customFunction = addGraphics;
        break;
      case "text":
        customFunction = addText;
        break;
      case "background":
        if (!generateVideo) {
          customFunction = addBackgroundImage;
        } else {
          if (objectRenderCompleted) {
            setObjectRendered();
          }
        }
        break;
      case "backgroundvideos":
        if (!generateVideo) {
          customFunction = addBackgroundVideo;
        } else {
          if (objectRenderCompleted) {
            setObjectRendered();
          }
        }
        break;
      case "backgroundcolor":
        if (!generateVideo) {
          customFunction = addBackgroundColor;
        } else {
          if (objectRenderCompleted) {
            setObjectRendered();
          }
        }
        break;
      default:
        break;
    }
    if (!customFunction) {
      return;
    }
    return customFunction(
      canvas,
      obj,
      dispatch,
      alreadyInitialized ?? true,
      previewMode,
      handleAnimationComplete,
      isStopped,
      generateVideo,
      scaleFactor,
      objectRenderCompleted ? setObjectRendered : setCurrentActiveIndex
    );
  };
  if (dispatch && !previewMode && !generateVideo) {
    dispatch(setProjectLoading(true));
  }
  const slideLoadingTimeout = setInterval(async () => {
    if (currentActiveIndex === canvasData.length) {
      clearTimeout(slideLoadingTimeout);
      if (dispatch && !previewMode && !generateVideo) {
        dispatch(setProjectLoading(false));
      }
    } else {
      if (loadingIndex === currentActiveIndex) {
        loadingIndex++;
        await setCanvasData(canvasData[currentActiveIndex]);
      }
    }
  }, 100);

  if (canvas)
    try {
      await new Promise((resolve) => {
        canvas.renderAll();
        resolve();
      });
    } catch (e) {}

  if (captureFrame)
    setTimeout(() => {
      captureFrame();
    }, 100);
};

export const sanitizedCanvasObject = (object, canvas) => {
  switch ((object.type || "").toLowerCase()) {
    case "avatar":
      return sanitizedAvatarObject(object, canvas);
    case "images":
      return sanitizedImageObject(object, canvas);
    case "graphics":
      return sanitizedGraphicsObject(object, canvas);
    case "text":
      return sanitizedTextObject(object, canvas);
    case "background":
      return sanitizedBackgroundObject(object, canvas);
    default:
      return;
  }
};

export const addCanvasMouseKeyboardEvent = async (e, canvas, dispatch) => {
  if (canvas === null) return;
  const activeObject = getActiveObject(canvas);

  if (!activeObject) {
    return;
  }
  if (
    (activeObject instanceof fabric.IText ||
      activeObject instanceof fabric.Textbox) &&
    activeObject.isEditing
  ) {
    return;
  }

  switch (e.keyCode) {
    // copy object
    case 67:
      copyObject(canvas);
      break;
    case 86:
      pastObject(canvas, dispatch);
      break;
    // remove
    case 46:
    case 8:
      if (activeObject.type === "avatar") {
        showToast("You can not remove the avatar.", "", "info");
        break;
      }
      removeObject(activeObject, canvas, dispatch);
      break;

    // move object
    case 37:
      activeObject.left -= 10;
      break;
    case 38:
      activeObject.top -= 10;
      break;
    case 39:
      activeObject.left += 10;
      break;
    case 40:
      activeObject.top += 10;
      break;
    default:
      break;
  }
  await new Promise((resolve) => {
    canvas.renderAll();
    resolve();
  });
  dispatch(generateCanvasImage(canvas));
  return dispatch(
    updateCanvasObject({
      id: activeObject.id,
      ...sanitizedCanvasObject(activeObject, canvas),
    })
  );
};

export let previewStartInterval;
let isGeneratingFrames = false;
const renderSlide = async (
  index,
  dispatch,
  canvas,
  slides,
  audioEl,
  handleComplete,
  isStopped,
  generateVideo,
  projectId,
  scaleFactor
) => {
  if (index === slides.length) {
    clearInterval(previewStartInterval);
    if (!generateVideo) {
      dispatch(setCurrentPreviewSlide(0));
    }
    handleComplete();
    return;
  }
  const currentSlide = slides[index];
  console.log(currentSlide, "currentSlide****************");
  if (!currentSlide) {
    clearInterval(previewStartInterval);
    return;
  }
  const prevSlideHasTransitions = !!(
    index && slides[index - 1]?.transitionData
  );

  const objectRenderCompleted = {
    isAudio: false, // audio complete
    isAnimated: false, // when all animations are completed
    isVideo: true, // video stops,
    objectLoaded: 0,
  };
  const test = [];
  // if all the keys are rendered then navigate to the next slide
  await setCanvasSlide(
    currentSlide,
    canvas,
    null,
    true,
    null,
    objectRenderCompleted,
    isStopped,
    generateVideo,
    scaleFactor
  );

  if (currentSlide.voiceFile && currentSlide.voiceFile.mp3 && audioEl) {
    audioEl.src = currentSlide.voiceFile.mp3.includes("vidboard.ai")
      ? currentSlide.voiceFile.mp3
      : AUDIO_FILE_BASE_URL + currentSlide.voiceFile.mp3;
    if (isStopped) {
      audioEl.pause();
    } else {
      audioEl.play();
      audioEl.onended = () => {
        audioEl.src = "";
        objectRenderCompleted.isAudio = true;
        audioEl.onended = null;
      };
    }
  } else {
    objectRenderCompleted.isAudio = true;
  }

  console.log("clear interval ****************", previewStartInterval);
  clearInterval(previewStartInterval);
  console.log("start interval ****************", previewStartInterval);
  previewStartInterval = setInterval(
    () => {
      const allObjectRendered =
        +objectRenderCompleted.objectLoaded ===
        (currentSlide.objects || []).length;
      const { objectLoaded, ...newObjectRenderComplete } =
        objectRenderCompleted;
      const isCompleted =
        allObjectRendered &&
        Object.values(newObjectRenderComplete).every((o) => !!o);

      const dataUrl = canvas.toDataURL("image/png");
      if (allObjectRendered) {
        test.push(dataUrl);
      }
      console.log(
        "isCompleted isGeneratingFrames ****************",
        isCompleted,
        isGeneratingFrames,
        objectRenderCompleted.objectLoaded,
        objectRenderCompleted
      );

      // checking test.length > 10.
      // test = screenshots of canvas.
      // some media assets take time to load so send req after 10 screenshots generate for the slide.
      if (isCompleted && !isGeneratingFrames /*  && test.length > 10 */) {
        const onComplete = async () => {
          const canvasObjects = getObjects(canvas);
          if (!!canvasObjects.length) {
            canvas.clear();
            if (!generateVideo) {
              canvas.setBackgroundColor("white", canvas.renderAll.bind(canvas));
            }
            await new Promise((resolve) => {
              canvas.renderAll();
              resolve();
            });
          }
          const nextIndex = index + 1;
          console.log(nextIndex, "nextIndex**************");

          if (!generateVideo) {
            dispatch(setCurrentPreviewSlide(nextIndex));
          }
          clearInterval(previewStartInterval);
          test.shift();
          return renderSlide(
            nextIndex,
            dispatch,
            canvas,
            slides,
            audioEl,
            handleComplete,
            isStopped,
            generateVideo,
            projectId,
            scaleFactor
          );
        };
        if (generateVideo) {
          isGeneratingFrames = true;
          return generateFrameForSlide(
            canvas,
            currentSlide,
            projectId,
            index,
            [...test],
            scaleFactor,
            dispatch,
            slides
          )
            .then((result) => {
              console.log("generate framed called ************");
              isGeneratingFrames = false;
              return onComplete();
            })
            .catch((err) => {
              console.log("error in frame ************", err);
              return clearInterval(previewStartInterval);
            });
        }
        return onComplete();
      }
    },
    generateVideo ? 24 : prevSlideHasTransitions ? 4000 : 2000
  );
};

const stopPreview = (canvas, audioEl, isStopped) => {
  if (audioEl) {
    audioEl.pause();
  }
  const animatedObjects = (canvas.getObjects() || []).filter(
    objectHasAnimation
  );

  animatedObjects.forEach((obj) =>
    obj.animate(
      { [obj.animation]: obj.left },
      {
        onChange: canvas.renderAll.bind(canvas),
        duration: 2000,
        onComplete: () => {},
        abort: () => !isStopped,
      }
    )
  );
  if (previewStartInterval) {
    clearInterval(previewStartInterval);
  }
  return;
};

export const startPreview = (
  currentPreviewSlide,
  dispatch,
  canvas,
  slides,
  audioEl,
  handleComplete,
  isStopped
) => {
  if (isStopped) {
    return stopPreview(canvas, audioEl, isStopped);
  }
  return renderSlide(
    currentPreviewSlide,
    dispatch,
    canvas,
    slides,
    audioEl,
    handleComplete,
    isStopped
  );
};

const generateFrameForSlide = async (
  canvas,
  currentSlide,
  projectId,
  slideIndex,
  test,
  scaleFactor = 1,
  dispatch,
  slides
) => {
  dispatch(setGenerateLoading(true));
  // copy to variable to add pro_avatar video url
  const slide = { ...currentSlide };
  slide.objects = [...(slide.objects || [])];

  return new Promise(async (resolve, reject) => {
    const userId = getFromLocal("userId");
    try {
      const avatarObjIndex = slide.objects.findIndex(
        (s) => s.type === "avatar"
      );
      if (avatarObjIndex > -1) {
        const avatarObj = { ...(slide.objects[avatarObjIndex] || {}) };
        // let proAvatarUrl;
        // if (avatarObj?.presenterId) {
        //   // adding pro_avatar key to the slide.
        //   proAvatarUrl = getCreateAvatarVideoBody(
        //     userId,
        //     projectId,
        //     slideIndex,
        //     slide.script,
        //     slide.voice,
        //     avatarObj?.presenterId
        //   );
        // }
        // avatarObj.pro_avatar = proAvatarUrl;
        slide.objects.splice(avatarObjIndex, 1, avatarObj);
        if (dispatch) {
          dispatch(updateObjectSlide({ data: avatarObj, slideIndex }));
        }
      }
      console.log("calling generationg /*****************", test);
      // Make API call to generate frame for the current slide
      const generateSlideFramesBody1 = await generateSlideFramesBody(
        userId,
        projectId,
        test,
        slide,
        slideIndex,
        canvas.getHeight(),
        canvas.getWidth(),
        scaleFactor
      );
      console.log("calling generationg /*****************");
      await generateSlideFrame(generateSlideFramesBody1);
      const loaderValue = (((slideIndex + 1) / slides.length) * 100).toFixed(2);
      dispatch(setLoaderValue(loaderValue));

      console.log("Frame generated for slide", slideIndex);
      resolve(); // Resolve the promise once the frame is generated
    } catch (error) {
      console.error("Error generating frame for slide", slideIndex, error);
      reject(error); // Reject the promise if there's an error
    }
  });
};

export const renderSlideForGeneration = async (
  canvas,
  slides,
  project,
  dispatch
) => {
  canvas.clear();

  const { id, scaleFactor } = project || {};
  const generateFramePromises = []; // Array to store promises for frame generation

  return renderSlide(
    0,
    dispatch,
    canvas,
    slides,
    null,
    () => dispatch(FianalGenerateVideo()),
    false,
    true,
    id,
    scaleFactor
  );

  // for (let index = 0; index < slides.length; index++) {
  //   const slide = slides[index];
  //   const objectRenderCompleted = {
  //     isAudio: false,
  //     isAnimated: false,
  //     isVideo: true, // @TODO update this when video is implemented for preview.
  //   };
  //   await setCanvasSlide(
  //     slide,
  //     canvas,
  //     null,
  //     true,
  //     null,
  //     objectRenderCompleted
  //   );
  //   setInterval(() => {
  //     const isCompleted = Object.values(objectRenderCompleted).every(
  //       (o) => !!o
  //     );

  //     if (isCompleted) {
  //       canvas.clear();
  //       canvas.renderAll();
  //       const nextIndex = index + 1;
  //       dispatch(setCurrentPreviewSlide(nextIndex));
  //       clearInterval(previewStartInterval);
  //     } else {
  //       const baseUrl = canvas.toDataURL("image/png");
  //       generateFramePromises.push(baseUrl);
  //     }
  //   }, 24);
  //   const generateFramePromise = generateFrameForSlide(
  //     canvas,
  //     slide,
  //     id,
  //     index
  //   );
  //   generateFramePromises.push(generateFramePromise);

  //   // Render the current slide on the canvas
  // }

  // Wait for all frame generation promises to resolve
  // await Promise.all(generateFramePromises);

  // Once all frames are generated, make additional API call if needed
};

export const getVideoElement = (url, canvas) => {
  const height = canvas?.getHeight();
  const width = canvas?.getWidth();

  var videoE = document.createElement("video");
  videoE.width = width;
  videoE.height = height;
  videoE.muted = true;
  videoE.style.display = "none";

  videoE.crossOrigin = "anonymous";

  var source = `<source src=${url} type='video/mp4' ></source>`;
  videoE.innerHTML = source;
  document.body.appendChild(videoE);

  return videoE;
};

export async function addBackgroundVideo(
  canvas,
  video,
  dispatch,
  alreadyInitialized = false,
  previewMode = false,
  handleAnimationComplete,
  isStopped,
  generateVideo,
  scaleFactor,
  setObjectRendered
) {
  if (!video.url) {
    console.error("video url missing");
    return;
  }
  const toggleLoader = (loading) => {
    if (!previewMode) {
      dispatch(setSlideLoading(!!loading));
    }
  };

  toggleLoader(true);

  const videoElement = getVideoElement(video.url, canvas);
  const canvasVideo = new fabric.CustomVideo(videoElement, {
    left: 0,
    top: 0,
    width: canvas.width,
    height: canvas.height,
    crossOrigin: "Anonymous",
  });

  const existingBg = findExistingObject(canvas, "background");
  const existingBgVideo = findExistingObject(canvas, "backgroundVideos");
  if (previewMode && existingBgVideo) {
    canvas.renderAll();
    if (setObjectRendered) {
      setObjectRendered();
    }
    return existingBgVideo;
  }
  if (existingBg) {
    canvasVideo.id = existingBg.id;
  } else if (existingBgVideo) {
    canvasVideo.id = existingBgVideo.id;
  } else if (video.id) {
    canvasVideo.id = video.id;
  } else {
    canvasVideo.id = generateUniqueId();
  }
  if (existingBg) {
    canvas.remove(existingBg);
  }
  if (existingBgVideo) {
    canvas.remove(existingBgVideo);
  }
  canvasVideo.type = "backgroundVideos";
  canvasVideo.opacity = 1;
  canvasVideo.url = video.url;
  if (!isStopped) {
    canvasVideo.getElement().play();
  } else {
    canvasVideo.getElement().pause();
  }

  canvas.setBackgroundImage(canvasVideo, canvas.renderAll.bind(canvas), {
    stretch: false,
    ...canvasVideo,
  });

  canvas.sendToBack(canvasVideo);
  if (!previewMode) {
    attachEventListeners(canvasVideo, canvas, dispatch);
  }

  // if (!previewMode) {
  canvasVideo.getElement().onloadeddata = function () {
    toggleLoader(false);
    if (setObjectRendered) {
      setObjectRendered();
    }
  };
  // }

  fabric.util.requestAnimFrame(async function render() {
    if (
      canvasVideo.getElement().currentTime <
      (previewMode ? canvasVideo.getElement().duration || 2 : 0.09)
    ) {
      await new Promise((resolve) => {
        canvas.renderAll();
        resolve();
      });
      fabric.util.requestAnimFrame(render);
    } else {
      canvasVideo.getElement().pause();
      if (!previewMode) {
        dispatch(generateCanvasImage(canvas));
      }
      toggleLoader();

      if (handleAnimationComplete) {
        handleAnimationComplete(video.id);
      }

      fabric.util.cancelAnimFrame(render);
    }
  });
  canvasVideo.hasControls = false;
  canvasVideo.selectable = !previewMode;
  if (!previewMode) {
    canvasVideo.lockMovementX = true;
    canvasVideo.lockMovementY = true;
    canvasVideo.lockRotation = true;
    canvasVideo.lockScalingX = true;
    canvasVideo.lockScalingY = true;
  }
  if (!previewMode) {
    const functionToExecute =
      existingBg || existingBgVideo || video.id
        ? updateCanvasObject
        : addCanvasObject;
    dispatch(
      functionToExecute(
        sanitizedBackgroundObject(
          {
            ...canvasVideo,
            id: canvasVideo.id,
            type: "backgroundVideos",
          },
          canvas
        )
      )
    );
  }
}
